import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  Calendar,
  CheckSquare,
  Inbox,
  LayoutDashboard,
  Settings,
  Users,
  Building2,
  GitPullRequest,
  Zap,
  Layers,
  Clock,
} from 'lucide-react'

import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command'
import { contactsApi, companiesApi } from '@/lib/api'
import { useProjects } from '@/hooks/useProjects'
import { useProjectScope } from '@/hooks/useProjectScope'
import type { Project } from '@/types/api'

type CommandEntry = {
  id: string
  label: string
  shortcut?: string
  icon: React.ElementType
  path: string
  group: string
}

type RecentItem = {
  id: string
  label: string
  path?: string
  kind: 'nav' | 'contact' | 'company' | 'scope'
  meta?: string
  scope?: string
}

type CommandPaletteProps = {
  projects?: Project[]
  scope?: string
  setScope?: (value: string) => void
}

const COMMANDS: CommandEntry[] = [
  {
    id: 'nav-home',
    label: 'Home',
    shortcut: 'G H',
    icon: LayoutDashboard,
    path: '/',
    group: 'Navigation',
  },
  {
    id: 'nav-inbox',
    label: 'Inbox',
    shortcut: 'G I',
    icon: Inbox,
    path: '/inbox',
    group: 'Navigation',
  },
  {
    id: 'nav-actions',
    label: 'Actions',
    shortcut: 'G A',
    icon: Zap,
    path: '/actions',
    group: 'Navigation',
  },
  {
    id: 'nav-calendar',
    label: 'Calendar',
    shortcut: 'G C',
    icon: Calendar,
    path: '/calendar',
    group: 'Navigation',
  },
  {
    id: 'nav-tasks',
    label: 'Tasks',
    shortcut: 'G T',
    icon: CheckSquare,
    path: '/tasks',
    group: 'Navigation',
  },
  {
    id: 'nav-pipelines',
    label: 'Pipelines',
    icon: GitPullRequest,
    path: '/pipelines',
    group: 'Pipelines',
  },
  {
    id: 'nav-contacts',
    label: 'Contacts',
    icon: Users,
    path: '/records/contacts',
    group: 'Records',
  },
  {
    id: 'nav-companies',
    label: 'Companies',
    icon: Building2,
    path: '/records/companies',
    group: 'Records',
  },
  {
    id: 'nav-settings',
    label: 'Settings',
    icon: Settings,
    path: '/settings',
    group: 'Settings',
  },
]

const COMMANDS_BY_ID = new Map(COMMANDS.map((command) => [command.id, command]))
const RECENT_STORAGE_KEY = 'go-crm:command:recent'
const RECENT_LIMIT = 6

const isRecentItem = (item: unknown): item is RecentItem => {
  if (!item || typeof item !== 'object') return false
  const record = item as Partial<RecentItem>
  if (typeof record.id !== 'string' || typeof record.label !== 'string') return false
  if (!record.kind) return false
  if (!['nav', 'contact', 'company', 'scope'].includes(record.kind)) return false
  if (record.kind === 'scope') {
    return typeof record.scope === 'string'
  }
  return typeof record.path === 'string'
}

const loadRecents = (): RecentItem[] => {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(RECENT_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((item) => isRecentItem(item))
  } catch {
    return []
  }
}

const persistRecents = (items: RecentItem[]) => {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(items))
  } catch {
    // Ignore storage write errors
  }
}

export function CommandPalette() {
  const { data: projectsResponse } = useProjects({ status: 'active', page_size: 50 })
  const projects = useMemo(() => projectsResponse?.items ?? [], [projectsResponse?.items])
  return <CommandPaletteBase projects={projects} />
}

export function CommandPaletteBase({
  projects: projectsProp,
  scope: scopeProp,
  setScope: setScopeProp,
}: CommandPaletteProps) {
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [recents, setRecents] = useState<RecentItem[]>(() => loadRecents())
  const navigate = useNavigate()
  const { t } = useTranslation()

  const projects = useMemo(() => projectsProp ?? [], [projectsProp])
  const { scope: scopeState, setScope: setScopeState } = useProjectScope(projects)
  const scope = scopeProp ?? scopeState
  const setScope = setScopeProp ?? setScopeState

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setSearchValue('')
      return
    }
    setRecents(loadRecents())
  }, [])

  // Track "g" key press for two-key shortcuts
  const [awaitingSecondKey, setAwaitingSecondKey] = useState(false)
  const awaitingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Don't handle shortcuts when typing in inputs
      const target = event.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      // Command palette shortcut
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        handleOpenChange(!open)
        return
      }

      // "C" for Create New (when not awaiting second key)
      if (!awaitingSecondKey && event.key.toLowerCase() === 'c' && !event.metaKey && !event.ctrlKey) {
        event.preventDefault()
        // Open command palette with search ready
        handleOpenChange(true)
        return
      }

      // Start "G + key" sequence
      if (event.key.toLowerCase() === 'g' && !event.metaKey && !event.ctrlKey && !awaitingSecondKey) {
        event.preventDefault()
        setAwaitingSecondKey(true)
        // Reset after 1.5 seconds
        if (awaitingTimeoutRef.current) {
          clearTimeout(awaitingTimeoutRef.current)
        }
        awaitingTimeoutRef.current = setTimeout(() => {
          setAwaitingSecondKey(false)
        }, 1500)
        return
      }

      // Handle second key in "G + key" sequence
      if (awaitingSecondKey) {
        setAwaitingSecondKey(false)
        if (awaitingTimeoutRef.current) {
          clearTimeout(awaitingTimeoutRef.current)
        }

        const shortcutMap: Record<string, string> = {
          'h': '/',           // G + H -> Home
          'i': '/inbox',      // G + I -> Inbox
          'a': '/actions',    // G + A -> Actions
          'c': '/calendar',   // G + C -> Calendar
          't': '/tasks',      // G + T -> Tasks
          's': '/settings',   // G + S -> Settings
          'p': '/pipelines/sales', // G + P -> Pipelines
          'o': '/records/contacts', // G + O -> Contacts (O for peOple)
          'b': '/records/companies', // G + B -> Companies (B for Business)
        }

        const key = event.key.toLowerCase()
        if (shortcutMap[key]) {
          event.preventDefault()
          navigate(shortcutMap[key])
        }
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      if (awaitingTimeoutRef.current) {
        clearTimeout(awaitingTimeoutRef.current)
      }
    }
  }, [open, handleOpenChange, awaitingSecondKey, navigate])

  const groupedCommands = useMemo(() => {
    return COMMANDS.reduce<Record<string, CommandEntry[]>>((acc, command) => {
      if (!acc[command.group]) {
        acc[command.group] = []
      }
      acc[command.group].push(command)
      return acc
    }, {})
  }, [])

  const searchQuery = searchValue.trim()
  const searchEnabled = open && searchQuery.length >= 2

  const { data: contactResults, isLoading: contactsLoading } = useQuery({
    queryKey: ['command-search-contacts', searchQuery],
    queryFn: () => contactsApi.list({ search: searchQuery, page_size: 6 }),
    enabled: searchEnabled,
    staleTime: 30_000,
  })

  const { data: companyResults, isLoading: companiesLoading } = useQuery({
    queryKey: ['command-search-companies', searchQuery],
    queryFn: () => companiesApi.list({ search: searchQuery, page_size: 6 }),
    enabled: searchEnabled,
    staleTime: 30_000,
  })

  const rememberRecent = (item: RecentItem) => {
    const next = [item, ...recents.filter((recent) => !(recent.id === item.id && recent.kind === item.kind))]
    const trimmed = next.slice(0, RECENT_LIMIT)
    setRecents(trimmed)
    persistRecents(trimmed)
  }

  const handleSelect = (path: string | undefined, recent?: RecentItem) => {
    if (!path) return
    handleOpenChange(false)
    navigate(path)
    if (recent) {
      rememberRecent(recent)
    }
  }

  const handleScopeSelect = (value: string, recent?: RecentItem) => {
    setScope(value)
    handleOpenChange(false)
    if (recent) {
      rememberRecent(recent)
    }
  }

  const contactEntries = (contactResults?.items ?? []).map((contact) => ({
    id: contact.id,
    label: contact.name || contact.email,
    meta: contact.email,
    icon: Users,
    path: `/records/contacts/${contact.id}`,
  }))

  const companyEntries = (companyResults?.items ?? []).map((company) => ({
    id: company.id,
    label: company.name,
    meta: company.domain ?? '',
    icon: Building2,
    path: '/records/companies',
  }))

  const projectEntries = useMemo(() => {
    if (searchQuery.length < 2) return []
    const normalized = searchQuery.toLowerCase()
    return projects
      .filter((project) => project.name.toLowerCase().includes(normalized))
      .slice(0, 6)
      .map((project) => ({
        id: project.id,
        label: project.name,
        color: project.color ?? '#64748b',
      }))
  }, [projects, searchQuery])

  const recentEntries = recents.map((recent) => {
    const navCommand = recent.kind === 'nav' ? COMMANDS_BY_ID.get(recent.id) : null
    const icon =
      navCommand?.icon ??
      (recent.kind === 'contact'
        ? Users
        : recent.kind === 'company'
        ? Building2
        : recent.kind === 'scope'
        ? Layers
        : Clock)
    return {
      ...recent,
      icon,
    }
  })

  return (
    <>
      {/* Keyboard shortcut hint */}
      {awaitingSecondKey && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] animate-in fade-in slide-in-from-bottom-2 duration-150">
          <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[hsl(var(--popover))] border border-[hsl(var(--border))] shadow-lg">
            <kbd className="px-2 py-1 rounded bg-[hsl(var(--muted))] text-sm font-medium text-[hsl(var(--foreground))]">G</kbd>
            <span className="text-sm text-[hsl(var(--muted-foreground))]">+</span>
            <span className="text-sm text-[hsl(var(--muted-foreground))]">
              <kbd className="px-1.5 py-0.5 rounded bg-[hsl(var(--muted))] text-xs">H</kbd> Home
              <span className="mx-2 text-[hsl(var(--border))]">|</span>
              <kbd className="px-1.5 py-0.5 rounded bg-[hsl(var(--muted))] text-xs">I</kbd> Inbox
              <span className="mx-2 text-[hsl(var(--border))]">|</span>
              <kbd className="px-1.5 py-0.5 rounded bg-[hsl(var(--muted))] text-xs">T</kbd> Tasks
              <span className="mx-2 text-[hsl(var(--border))]">|</span>
              <kbd className="px-1.5 py-0.5 rounded bg-[hsl(var(--muted))] text-xs">C</kbd> Calendar
            </span>
          </div>
        </div>
      )}

      <CommandDialog open={open} onOpenChange={handleOpenChange}>
        <CommandInput
          placeholder={t('commandPalette.input')}
          value={searchValue}
          onValueChange={setSearchValue}
        />
        <CommandList>
          <CommandEmpty>{t('commandPalette.empty')}</CommandEmpty>

          {recentEntries.length > 0 && searchQuery.length === 0 ? (
            <>
              <CommandGroup heading={t('commandPalette.group.recent')}>
                {recentEntries.map((entry) => (
                  <CommandItem
                    key={`recent-${entry.kind}-${entry.id}`}
                    value={[entry.label, entry.meta].filter(Boolean).join(' ')}
                    onSelect={() =>
                      entry.kind === 'scope'
                        ? handleScopeSelect(entry.scope ?? 'all', entry)
                        : handleSelect(entry.path, entry)
                    }
                  >
                    <entry.icon className="h-4 w-4" />
                    <span>{entry.label}</span>
                    {entry.meta ? (
                      <span className="ml-auto text-xs text-[hsl(var(--muted-foreground))]">{entry.meta}</span>
                    ) : null}
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
            </>
          ) : null}

          {searchQuery.length >= 2 ? (
            <>
              <CommandGroup heading={t('commandPalette.group.contacts')}>
                {contactEntries.length === 0 ? (
                  <CommandItem disabled value="no-contacts">
                    <Users className="h-4 w-4" />
                    <span>
                      {contactsLoading
                        ? t('commandPalette.loading')
                        : t('commandPalette.noContacts')}
                    </span>
                  </CommandItem>
                ) : (
                  contactEntries.map((entry) => (
                    <CommandItem
                      key={`contact-${entry.id}`}
                      value={[entry.label, entry.meta].filter(Boolean).join(' ')}
                      onSelect={() =>
                        handleSelect(entry.path, {
                          id: entry.id,
                          label: entry.label ?? '',
                          path: entry.path,
                          kind: 'contact',
                          meta: entry.meta,
                        })
                      }
                    >
                      <entry.icon className="h-4 w-4" />
                      <span>{entry.label}</span>
                      {entry.meta ? (
                        <span className="ml-auto text-xs text-[hsl(var(--muted-foreground))]">{entry.meta}</span>
                      ) : null}
                    </CommandItem>
                  ))
                )}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup heading={t('commandPalette.group.companies')}>
                {companyEntries.length === 0 ? (
                  <CommandItem disabled value="no-companies">
                    <Building2 className="h-4 w-4" />
                    <span>
                      {companiesLoading
                        ? t('commandPalette.loading')
                        : t('commandPalette.noCompanies')}
                    </span>
                  </CommandItem>
                ) : (
                  companyEntries.map((entry) => (
                    <CommandItem
                      key={`company-${entry.id}`}
                      value={[entry.label, entry.meta].filter(Boolean).join(' ')}
                      onSelect={() =>
                        handleSelect(entry.path, {
                          id: entry.id,
                          label: entry.label,
                          path: entry.path,
                          kind: 'company',
                          meta: entry.meta,
                        })
                      }
                    >
                      <entry.icon className="h-4 w-4" />
                      <span>{entry.label}</span>
                      {entry.meta ? (
                        <span className="ml-auto text-xs text-[hsl(var(--muted-foreground))]">{entry.meta}</span>
                      ) : null}
                    </CommandItem>
                  ))
                )}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup heading={t('commandPalette.group.projects')}>
                {projectEntries.length === 0 ? (
                  <CommandItem disabled value="no-projects">
                    <Layers className="h-4 w-4" />
                    <span>{t('commandPalette.noProjects')}</span>
                  </CommandItem>
                ) : (
                  projectEntries.map((entry) => (
                    <CommandItem
                      key={`project-${entry.id}`}
                      value={entry.label}
                      onSelect={() =>
                        handleScopeSelect(entry.id, {
                          id: entry.id,
                          label: entry.label,
                          kind: 'scope',
                          scope: entry.id,
                          meta: t('commandPalette.group.scope'),
                        })
                      }
                    >
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: entry.color }}
                      />
                      <span>{entry.label}</span>
                      <span className="ml-auto text-xs text-[hsl(var(--muted-foreground))]">
                        {t('commandPalette.group.scope')}
                      </span>
                    </CommandItem>
                  ))
                )}
              </CommandGroup>
              <CommandSeparator />
            </>
          ) : null}

          {Object.entries(groupedCommands).map(([group, commands], index) => (
            <div key={group}>
              <CommandGroup heading={t(`commandPalette.group.${group}`, group)}>
                {commands.map((command) => {
                  const Icon = command.icon
                  return (
                    <CommandItem
                      key={command.id}
                      value={command.label}
                      onSelect={() =>
                        handleSelect(command.path, {
                          id: command.id,
                          label: command.label,
                          path: command.path,
                          kind: 'nav',
                        })
                      }
                    >
                      <Icon className="h-4 w-4" />
                      <span>{t(`commandPalette.item.${command.id}`, command.label)}</span>
                      {command.shortcut ? (
                        <CommandShortcut>{command.shortcut}</CommandShortcut>
                      ) : null}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
              {index < Object.keys(groupedCommands).length - 1 ? <CommandSeparator /> : null}
            </div>
          ))}

          <CommandSeparator />
          <CommandGroup heading={t('commandPalette.group.scope')}>
            <CommandItem
              value={t('commandPalette.scope.all')}
              onSelect={() =>
                handleScopeSelect('all', {
                  id: 'scope-all',
                  label: t('commandPalette.scope.all'),
                  kind: 'scope',
                  scope: 'all',
                  meta: t('commandPalette.group.scope'),
                })
              }
            >
              <Layers className="h-4 w-4" />
              <span>{t('commandPalette.scope.all')}</span>
              {scope === 'all' ? <CommandShortcut>✓</CommandShortcut> : null}
            </CommandItem>
            {projects.map((project) => (
              <CommandItem
                key={project.id}
                value={project.name}
                onSelect={() =>
                  handleScopeSelect(project.id, {
                    id: project.id,
                    label: project.name,
                    kind: 'scope',
                    scope: project.id,
                    meta: t('commandPalette.group.scope'),
                  })
                }
              >
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: project.color ?? '#64748b' }}
                />
                <span>{project.name}</span>
                {scope === project.id ? <CommandShortcut>✓</CommandShortcut> : null}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  )
}
